// ── Organisation Hierarchy ──────────────────────────────────────────

export type OrgType = 'kinetica' | 'integrator' | 'client';
export type Tier = 'base' | 'pro' | 'enterprise';
export type UserRole = 'admin' | 'engineer' | 'operator' | 'viewer';
export type BillingStatus = 'active' | 'grace' | 'suspended';

export interface Organisation {
  org_id: string;
  org_type: OrgType;
  parent_org_id: string | null;
  org_name: string;
  xero_contact_id: string;
  tier: Tier;
  device_serials: string[];
  region: string;
  created_at: string;
}

// ── Auth / User ─────────────────────────────────────────────────────

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  org_id: string;
  org_type: OrgType;
  org_name: string;
  role: UserRole;
  tier: Tier;
  features: string[];
}

export interface Operator {
  sub: string;
  email: string;
  name: string;
  role: UserRole;
  status: 'active' | 'invited' | 'disabled';
  last_login: string | null;
}

// ── Fleet / Devices ─────────────────────────────────────────────────

export type DeviceStatus = 'online' | 'idle' | 'faulted' | 'offline';

// Device kind discriminator. Default 'arm' for all existing devices; the fleet
// is generalising beyond articulated arms (scara/delta/gantry) and controllers.
export type DeviceType = 'arm' | 'scara' | 'delta' | 'gantry' | 'controller';

export interface Device {
  serial: string;
  name: string;
  org_id: string;
  org_name: string;
  status: DeviceStatus;
  firmware_version: string;
  model_version: string;
  uptime_hours: number;
  cycle_count: number;
  last_seen: string;
  location: string;
  device_type?: DeviceType;
}

export interface DeviceTelemetry {
  serial: string;
  timestamp: string;
  joint_positions: number[];
  joint_temperatures: number[];
  cycle_time_ms: number;
  pick_success_rate: number;
  cpu_temp: number;
  power_watts: number;
}

export interface UnassignedDevice {
  serial: string;
  name: string;
  status: DeviceStatus;
  firmware_version: string;
  last_seen: string;
  location: string;
  device_type?: DeviceType;
}

// ── Billing ─────────────────────────────────────────────────────────

export interface Subscription {
  tier: Tier;
  features: string[];
  billing_status: BillingStatus;
  valid_until: string;
  arm_count: number;
  device_count?: number;
  usage_cycles: number;
  usage_training_runs: number;
  last_month_cycles: number;
  last_month_training_runs: number;
  last_monthly_reset: string;
  included_training_runs: number;
}

export interface Invoice {
  invoice_id: string;
  date: string;
  due_date: string;
  total: number;
  currency: string;
  status: 'PAID' | 'AUTHORISED' | 'OVERDUE' | 'DRAFT';
  line_items: InvoiceLineItem[];
}

export interface InvoiceLineItem {
  item_code: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

// ── Training / Models ───────────────────────────────────────────────

// Phase 3: jobs come from the Fastify training queue (/api/telemetry/training).
export type TrainingStatus =
  | 'queued' | 'launching' | 'training' | 'quantising' | 'completed' | 'failed' | 'canceled';

export interface TrainingJob {
  id: string;
  org_id: string;
  sku_id: string;
  sku_code: string;
  dataset_version: string;
  base_model: string;
  image_count: number;
  class_count: number;
  status: TrainingStatus;
  launch_attempts: number;
  created_at: string;
  claimed_at?: string;
  started_at?: string;
  completed_at?: string;
  model_key?: string;
  metrics?: TrainingMetrics | null;
  progress?: TrainingProgress | null;
  error?: string;
  created_by: string;
  updated_at: string;
}

export interface TrainingMetrics {
  map50: number;
  map50_95?: number;
  precision?: number;
  recall?: number;
}

// Live epoch progress on an in-flight job. `at` marks when the epoch was observed,
// not when the record was last touched — the driver only writes on an epoch change.
// Percentage and ETA are deliberately NOT stored; derive them at render time so
// they stay honest against a record that may be a few minutes old.
export interface TrainingProgress {
  epoch: number;
  total_epochs: number;
  at: string;
  losses?: Record<string, number>;
}

// ── Feature Flags ───────────────────────────────────────────────────

export const FEATURE_FLAGS = {
  REMOTE_ACCESS: 'remote_access',
  OTA: 'ota',
  ML_VISION: 'ml_vision',
  TELEMETRY_ARCHIVE: 'telemetry_archive',
  PRIORITY_SUPPORT: 'priority_support',
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// ── OTA / Firmware Updates ─────────────────────────────────────────

export interface OTAJob {
  job_id: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'DELETION_IN_PROGRESS';
  target_selection: 'CONTINUOUS' | 'SNAPSHOT';
  description: string;
  created_at: string;
  completed_at?: string;
  last_updated_at?: string;
  targets: number;
  succeeded: number;
  failed: number;
  in_progress: number;
  queued: number;
}

export interface DeviceOTAStatus {
  serial: string;
  name: string;
  current_firmware: string;
  target_firmware?: string;
  job_id?: string;
  execution_status?: 'QUEUED' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'REJECTED' | 'CANCELED';
  last_updated?: string;
  progress_pct?: number;
  is_online?: boolean;
  failure_reason?: string;
}

// ── Telemetry (Phase 0 server) ──────────────────────────────────────

// NOTE: no `promql` field — the server deliberately projects the internal
// PromQL template out of the /telemetry/metrics response (test-enforced
// server-side).
export interface TelemetryMetric {
  id: string;
  label: string;
  unit: string;
  source: 'prom' | 'loki';
}

export interface TelemetryQueryRequest {
  metric: string;
  serials?: string[];
  start: number;
  end: number;
  step: number;
}

export interface TelemetryFaultsRequest {
  serials?: string[];
  start: number;
  end: number;
}

// WIRE-FORMAT types: the server passes Grafana's raw result arrays through
// unchanged (`{ series: res.data.result }` / `{ events: res.data.result }`),
// so these mirror the Prometheus matrix and Loki streams formats verbatim.
// Reshaping to friendlier point structs is Phase 2 (dashboard builder) work.
export interface PromMatrixEntry {
  metric: Record<string, string>;        // labels, e.g. { serial: "KIN-A001" }
  values: Array<[number, string]>;       // [unixSeconds, valueAsString]
}
export interface TelemetryQueryResponse { series: PromMatrixEntry[]; }

export interface LokiStreamEntry {
  stream: Record<string, string>;        // labels, e.g. { serial: "...", level: "error" }
  values: Array<[string, string]>;       // [unixNanosAsString, logLine]
}
export interface TelemetryFaultsResponse { events: LokiStreamEntry[]; }
